import { useEffect, useState, useCallback, FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  styled,
  Card,
  CardContent,
  Stack,
  Chip,
  Avatar,
  Skeleton,
} from '@mui/material';
import Person2Icon from '@mui/icons-material/Person2';
import { Work, Schedule, Notes, Edit } from '@mui/icons-material';
import debounce from 'lodash/debounce';

import { Application } from '@libs/models';
import { ApplicationStatus } from '@libs/types';
import { routes } from '@carcareers/utils';
import { ApplicationStatusChip, DashboardPageContainer } from '@carcareers/components';
import { FormProvider, useForm } from '@carcareers/contexts';
import {
  useApplicationViewQuery,
  useAdminUpdateApplicationMutation,
  useUpdateApplicationMutation,
} from '@carcareers/store';
import { useIsMobile } from '@carcareers/utils';

const ColorInput = styled('input')(({ theme }) => ({
  width: '39px',
  height: '39px',
  padding: 0,
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  '&::-webkit-color-swatch-wrapper': {
    padding: 0,
  },
  '&::-webkit-color-swatch': {
    border: 'none',
    borderRadius: theme.shape.borderRadius,
  },
  '&::-moz-color-swatch': {
    border: 'none',
    borderRadius: theme.shape.borderRadius,
  },
}));

interface ApplicationViewProps {
  isAdmin?: boolean;
}

export const ApplicationView: FC<ApplicationViewProps> = ({ isAdmin = false }) => {
  const navigate = useNavigate();
  const params = useParams();
  const isMobile = useIsMobile();

  const applicationId = params.applicationId ? parseInt(params.applicationId) : undefined;

  const { data: application, isLoading } = useApplicationViewQuery(applicationId ?? 0, { skip: !applicationId });
  const [updateApplication, { isLoading: isUpdating }] = useAdminUpdateApplicationMutation();
  const [updateApplicationEmployer, { isLoading: isUpdatingEmployer }] = useUpdateApplicationMutation();
  const jobseekerProfileLink = useMemo(
    () =>
      isAdmin
        ? `/${routes.ADMIN_HOME}/${routes.ADMIN_JOBSEEKERS_VIEW.replace(':id', `${application?.jobseeker?.id}`)}`
        : `/${routes.EMPLOYER_HOME}/${routes.EMPLOYER_CANDIDATES_VIEW.replace(':id', `${application?.jobseeker?.id}`)}`,
    [application?.jobseeker?.id, isAdmin],
  );

  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const getDaysAgo = (date?: Date | string) => {
    if (!date) return 0;
    const diffTime = Math.abs(new Date().getTime() - new Date(date).getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  useEffect(() => {
    if (!applicationId) {
      navigate(isAdmin ? routes.ADMIN_HOME : routes.EMPLOYER_HOME);
    }
  }, [applicationId, navigate, isAdmin]);

  const handleSaveNotes = async (formData: Application) => {
    if (!formData || !formData?.id) return;

    try {
      await (isAdmin ? updateApplication : updateApplicationEmployer)({
        id: formData?.id,
        data: {
          notes: formData?.notes,
        },
      }).unwrap();
    } catch (error) {
      console.error('Failed to save notes:', error);
    }
  };

  const handleStatusChange = async (formData: Application, newStatus: ApplicationStatus) => {
    if (!formData || !formData?.id) return;

    if (newStatus === ApplicationStatus.CUSTOM) {
      return;
    }

    try {
      await (isAdmin ? updateApplication : updateApplicationEmployer)({
        id: formData?.id,
        data: {
          status: newStatus,
          customStatus: '',
          statusColor: undefined,
        },
      }).unwrap();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleCustomStatusSave = async (formData: Application, setDataFn: (path: string, value: unknown) => void) => {
    if (!formData || !formData?.id) return;

    try {
      await (isAdmin ? updateApplication : updateApplicationEmployer)({
        id: formData?.id,
        data: {
          status: ApplicationStatus.CUSTOM,
          customStatus: formData?.customStatus,
          statusColor: formData?.statusColor,
        },
      }).unwrap();

      setDataFn('status', ApplicationStatus.CUSTOM);
      setDataFn('customStatus', formData?.customStatus);
      setDataFn('statusColor', formData?.statusColor);
    } catch (error) {
      console.error('Failed to save custom status:', error);
    }
  };

  const ApplicationDetails = () => {
    const { data: formData, setData, getData } = useForm<Application>();
    const [customStatusEditMode, setCustomStatusEditMode] = useState(false);
    const [localColor, setLocalColor] = useState(formData?.statusColor || '#000000');

    const debouncedColorChange = useCallback(
      debounce((color: string) => {
        setData('statusColor', color);
      }, 300),
      [setData],
    );

    useEffect(() => {
      if (formData?.statusColor) {
        setLocalColor(formData?.statusColor);
      }
    }, [formData?.statusColor]);

    return (
      <>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={3} alignItems={isMobile ? 'flex-start' : 'center'}>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: 'primary.main',
                  fontSize: '1.5rem',
                  fontWeight: 600,
                }}
              >
                {isLoading
                  ? ''
                  : getInitials(formData?.jobseeker?.user?.firstName, formData?.jobseeker?.user?.lastName)}
              </Avatar>
              <Stack spacing={1} sx={{ flex: 1 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {isLoading ? (
                      <Skeleton variant="text" width={300} height={32} />
                    ) : formData?.jobseeker?.user ? (
                      `${formData?.jobseeker.user.firstName} ${formData?.jobseeker.user.lastName}`
                    ) : (
                      'Unknown Candidate'
                    )}
                  </Typography>
                  {!isLoading && formData?.jobseeker?.id && (
                    <Button
                      startIcon={<Person2Icon />}
                      variant="outlined"
                      onClick={() =>
                        navigate(jobseekerProfileLink)
                      }
                      sx={{ borderRadius: 2 }}
                    >
                      View Profile
                    </Button>
                  )}
                </Stack>
                <Typography variant="body1" color="text.secondary">
                  {isLoading ? (
                    <Skeleton variant="text" width={200} height={20} />
                  ) : (
                    formData?.post?.job?.title || 'Unknown Position'
                  )}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                  {isLoading ? (
                    <Skeleton variant="rounded" width={100} height={32} />
                  ) : (
                    formData?.status && (
                      <ApplicationStatusChip
                        status={formData?.status}
                        customStatus={formData?.customStatus}
                        statusColor={formData?.statusColor}
                      />
                    )
                  )}
                  {isLoading ? (
                    <Skeleton variant="rounded" width={150} height={32} />
                  ) : (
                    <Chip
                      icon={<Schedule />}
                      label={`Applied ${new Date(formData?.createdAt || '').toLocaleDateString()}`}
                      variant="outlined"
                      sx={{ px: 1 }}
                    />
                  )}
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Schedule sx={{ color: 'primary.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                {isLoading ? (
                  <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
                ) : (
                  getDaysAgo(formData?.createdAt)
                )}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Days Since Applied
              </Typography>
            </CardContent>
          </Card>
          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 3,
              backgroundColor: 'background.paper',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  backgroundColor: 'secondary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Work sx={{ color: 'secondary.contrastText', fontSize: 28 }} />
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: 'secondary.main', mb: 1 }}>
                {isLoading ? (
                  <Box sx={{ width: 40, height: 32, bgcolor: 'grey.200', borderRadius: 1, mx: 'auto' }} />
                ) : (
                  formData?.post?.job?.rooftop?.name
                )}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Rooftop
              </Typography>
            </CardContent>
          </Card>
          {formData?.withdrawnAt && (
            <Card
              elevation={0}
              sx={{
                flex: 1,
                borderRadius: 3,
                backgroundColor: 'background.paper',
                height: '100%',
              }}
            >
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    backgroundColor: 'error.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <Schedule sx={{ color: 'error.contrastText', fontSize: 28 }} />
                </Box>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'error.main', mb: 1 }}>
                  {getDaysAgo(formData?.withdrawnAt)}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  Days Since Withdrawn
                </Typography>
              </CardContent>
            </Card>
          )}
        </Stack>

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Edit sx={{ color: 'secondary.main' }} />
                Update Status
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Button
                  variant={formData?.status === ApplicationStatus.WAITING ? 'contained' : 'outlined'}
                  color="secondary"
                  onClick={() => {
                    setData('status', ApplicationStatus.WAITING);
                    handleStatusChange(formData, ApplicationStatus.WAITING);
                  }}
                  disabled={isUpdating || isUpdatingEmployer}
                  sx={{ borderRadius: 2 }}
                >
                  Waiting
                </Button>
                <Button
                  variant={formData?.status === ApplicationStatus.REVIEWED ? 'contained' : 'outlined'}
                  color="secondary"
                  onClick={() => {
                    setData('status', ApplicationStatus.REVIEWED);
                    handleStatusChange(formData, ApplicationStatus.REVIEWED);
                  }}
                  disabled={isUpdating || isUpdatingEmployer}
                  sx={{ borderRadius: 2 }}
                >
                  Reviewed
                </Button>
                <Button
                  variant={formData?.status === ApplicationStatus.CONTACTED ? 'contained' : 'outlined'}
                  color="secondary"
                  onClick={() => {
                    setData('status', ApplicationStatus.CONTACTED);
                    handleStatusChange(formData, ApplicationStatus.CONTACTED);
                  }}
                  disabled={isUpdating || isUpdatingEmployer}
                  sx={{ borderRadius: 2 }}
                >
                  Contacted
                </Button>
                <Button
                  variant={formData?.status === ApplicationStatus.SET ? 'contained' : 'outlined'}
                  color="secondary"
                  onClick={() => {
                    setData('status', ApplicationStatus.SET);
                    handleStatusChange(formData, ApplicationStatus.SET);
                  }}
                  disabled={isUpdating || isUpdatingEmployer}
                  sx={{ borderRadius: 2 }}
                >
                  Set
                </Button>

                {customStatusEditMode ? (
                  <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    <TextField
                      size="small"
                      label="Custom Status"
                      value={getData('customStatus') || ''}
                      onChange={(e) => setData('customStatus', e.target.value.slice(0, 25))}
                      sx={{ minWidth: 150 }}
                      slotProps={{
                        htmlInput: { maxLength: 25 },
                        input: {
                          sx: {
                            borderRadius: 2,
                          },
                        },
                      }}
                    />
                    <Typography variant="subtitle2">{`${(getData('customStatus') || '').length}/25`}</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 2 }}>
                      <ColorInput
                        type="color"
                        value={localColor}
                        onChange={(e) => {
                          const newColor = e.target.value;
                          setLocalColor(newColor);
                          debouncedColorChange(newColor);
                        }}
                        sx={{
                          borderRadius: 3,
                        }}
                      />
                    </Box>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={!getData('customStatus')}
                      onClick={() => {
                        handleCustomStatusSave(formData, setData);
                        setCustomStatusEditMode(false);
                      }}
                      sx={{ borderRadius: 2 }}
                    >
                      Save
                    </Button>
                    <Button variant="text" onClick={() => setCustomStatusEditMode(false)} sx={{ borderRadius: 2 }}>
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant={formData?.status === ApplicationStatus.CUSTOM ? 'contained' : 'outlined'}
                    onClick={() => {
                      if (formData?.status !== ApplicationStatus.CUSTOM) {
                        setData('status', ApplicationStatus.CUSTOM);
                      }
                      setCustomStatusEditMode(true);
                    }}
                    disabled={isUpdating || isUpdatingEmployer}
                    color="secondary"
                    sx={{ borderRadius: 2 }}
                  >
                    {formData?.status === ApplicationStatus.CUSTOM
                      ? `${formData?.customStatus || 'Custom'}`
                      : 'Custom Status...'}
                  </Button>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            backgroundColor: 'background.paper',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Notes sx={{ color: 'primary.main' }} />
                Notes
              </Typography>
            </Box>
            <Box sx={{ p: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Notes"
                value={getData('notes') || ''}
                onChange={(e) => setData('notes', e.target.value)}
                disabled={isUpdating || isUpdatingEmployer}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                onClick={() => handleSaveNotes(formData)}
                disabled={isUpdating || isUpdatingEmployer}
                sx={{ borderRadius: 2 }}
              >
                Save Notes
              </Button>
            </Box>
          </CardContent>
        </Card>
      </>
    );
  };

  const candidateName = application?.jobseeker?.user
    ? `${application.jobseeker.user.firstName} ${application.jobseeker.user.lastName}`
    : 'Unknown Candidate';
  const jobTitle = application?.post?.job?.title;

  return (
    <FormProvider initialData={application}>
      <DashboardPageContainer idReplacement={candidateName} nestedIdReplacement={jobTitle} loading={isLoading}>
        <ApplicationDetails />
      </DashboardPageContainer>
    </FormProvider>
  );
};

export default ApplicationView;
